import isEqual from 'lodash/isEqual';
import { ReactElement, useCallback, useEffect, useRef, useState } from 'react';

import useMemoCompare from '~/hooks/useMemoCompare';
import api from '~/services/api';
import { useStoreActions, useStoreState } from '~/store/hooks';
import useRole from '~/store/user/hooks/useRole';
import { extractErrorMessage } from '~/utils/error/error';
import Table from '../../reusable/Table';

import { IDictionaryTypes } from '~/services/api/dictionaries/types';

import styles from './MyClients.module.scss';

const MyClients = (): ReactElement => {
  const [dictionaries, setDictionaries] = useState<{ id: number; value: string }[]>([]);
  const lastBootstrapClientsKeyRef = useRef<string | null>(null);

  const clinicId = useStoreState(state => state.clinic.current?.id);

  const { team, isGlobalUser } = useRole();

  const { id: teamId, name } = team || {};

  const {
    list: clients,
    pagination: { pageSize, pageNumber },
    filters,
  } = useStoreState(state => state.client);

  const memoizedFilters = useMemoCompare(filters, (prev, next) => prev && isEqual(prev, next));

  const onGetMyClinic = useStoreActions(actions => actions.clinic.onGetMyClinic);
  const { onGetTeamClients, onGetClinicClients } = useStoreActions(actions => actions.client);
  const { showError } = useStoreActions(actions => actions.snackbar);
  const { onGetActTeamsAvailable } = useStoreActions(action => action.actTeam);

  const onMount = useCallback(async () => {
    try {
      if (!clinicId) {
        await onGetMyClinic();
        return;
      }

      if (isGlobalUser) {
        await onGetActTeamsAvailable({ clinicId: String(clinicId) });

        const clinicPayload = {
          clinicId: String(clinicId),
          teamIds: memoizedFilters.teamIds?.map(id => String(id)),
          params: { ...memoizedFilters, pageSize, pageNumber },
        };
        const key = `g:${JSON.stringify(clinicPayload)}`;
        if (lastBootstrapClientsKeyRef.current !== key) {
          await onGetClinicClients(clinicPayload);
          lastBootstrapClientsKeyRef.current = key;
        }
      } else if (teamId) {
        const teamPayload = {
          clinicId: String(clinicId),
          teamId: String(teamId),
          params: { ...memoizedFilters, pageSize, pageNumber },
        };
        const key = `t:${JSON.stringify(teamPayload)}`;
        if (lastBootstrapClientsKeyRef.current !== key) {
          await onGetTeamClients(teamPayload);
          lastBootstrapClientsKeyRef.current = key;
        }
      }

      if (!dictionaries.length) {
        const { data } = await api.dictionaries.getAvailableTypeList(
          IDictionaryTypes.DischargeReason,
        );
        setDictionaries(data);
      }
    } catch (e) {
      showError(extractErrorMessage(e));
    }
    // Filters / pagination changes are handled by Table (AutoSubmit); deps only for bootstrap.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    clinicId,
    dictionaries.length,
    isGlobalUser,
    onGetActTeamsAvailable,
    onGetClinicClients,
    onGetMyClinic,
    onGetTeamClients,
    showError,
    teamId,
  ]);

  useEffect(() => {
    onMount();
  }, [onMount]);

  return (
    <div>
      <div className={styles.header}>
        <h2>{`${name ? `${name} ` : ''}Clients`}</h2>
      </div>
      <Table
        teamId={teamId}
        showTeamSelect={isGlobalUser}
        list={clients}
        onGetClients={isGlobalUser ? onGetClinicClients : onGetTeamClients}
        clinicId={clinicId}
      />
    </div>
  );
};

export default MyClients;
